import "./app.scss";
import { auth, isActivated } from "portal/utils/auth";
import { Breakpoint, useBreakpoint } from "./utils/hooks/useBreakpoint";
import { classes } from "portal/theme/theme";
import { EnvironmentFlag } from "portal/components/EnvironmentFlag";
import { Helmet } from "react-helmet";
import { KeyboardShortcutsDialog } from "portal/components/KeyboardShortcutsDialog";
import { Loading } from "portal/components/Loading";
import { LOCALSTORAGE_DRAWER_EXPANDED } from "portal/utils/localStorage";
import { Outlet, useLocation } from "react-router-dom";
import { Path } from "./utils/routing";
import { PendingActivationOverlay } from "portal/components/PendingActivationOverlay";
import { resolveToClosestPortalLang, setLocale } from "common/utils/i18n";
import { setSelf } from "portal/state/self";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { useLocalStorage } from "@uidotdev/usehooks";
import { usePostHog } from "portal/utils/postHog/usePostHog";
import { useQueryPopups } from "./utils/hooks/useApiPopups";
import { userApi } from "common/state/portalApi/user";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import CssBaseline from "@mui/material/CssBaseline";
import React, { ReactElement, useEffect } from "react";

const App = (): ReactElement => {
  /**
   * Handle background user auth
   */
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    user: auth0User,
    isLoading: isAuth0UserLoading,
    getAccessTokenSilently,
    error,
  } = useAuth0();
  const { data: userResponse } = useQueryPopups(
    userApi.endpoints.getUser.useQuery(
      auth0User?.sub ? { userId: auth0User.sub } : skipToken
    )
  );
  const { user, isLoading: isCarbonUserLoading } = useSelf();
  const { pathname } = useLocation();
  const isLoading =
    isAuth0UserLoading || (isAuthenticated && isCarbonUserLoading);

  const { i18n } = useTranslation();
  // if there is a saved language on the user, use it
  useEffect(() => {
    const locale = resolveToClosestPortalLang(
      userResponse?.user?.userMetadata?.language
    );
    if (locale) {
      setLocale(i18n, locale);
    }
  }, [i18n, userResponse?.user?.userMetadata?.language]);

  const breakpoint = useBreakpoint();
  const isSmall = breakpoint <= Breakpoint.sm;

  auth.setAccessTokenSilently(getAccessTokenSilently);
  useEffect(() => {
    if (userResponse) {
      dispatch(setSelf(userResponse));
    }
  }, [dispatch, userResponse]);

  const [isExpanded] = useLocalStorage<boolean>(
    LOCALSTORAGE_DRAWER_EXPANDED,
    false
  );

  usePostHog();

  // don't let pending users see UI
  if (pathname !== Path.LOGOUT && user && !isActivated(user)) {
    console.debug("App.tsx:unactivated");
    return <PendingActivationOverlay />;
  }

  if (isLoading || error) {
    console.debug("App.tsx:Loading: isLoading=", isLoading, "error=", error);
    return <Loading failed={Boolean(error)} error={error} overlay />;
  }

  console.debug("App.tsx:mainpath:", window.location.pathname);
  return (
    <>
      <CssBaseline />
      <EnvironmentFlag />
      <Helmet>
        {/* proper noun, no localization required */}
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <title>Carbon Robotics</title>
      </Helmet>
      <div
        className={classes(
          "h-screen",
          "flex flex-col items-center justify-start",
          {
            "pl-16": !isSmall && !isExpanded,
            "pl-60": !isSmall && isExpanded,
          }
        )}
      >
        <Outlet />
      </div>
      <KeyboardShortcutsDialog />
    </>
  );
};

export default App;
